import { useTranslation } from 'react-i18next'

import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { cn } from '@/lib/cn'

export type IdpClientScopeDetailTab = 'mappers' | 'settings'

const tabIds: Record<IdpClientScopeDetailTab, { panelId: string; tabId: string }> = {
  mappers: {
    panelId: 'oidc-client-scope-mappers-panel',
    tabId: 'oidc-client-scope-mappers-tab',
  },
  settings: {
    panelId: 'oidc-client-scope-settings-panel',
    tabId: 'oidc-client-scope-settings-tab',
  },
}

export function IdpClientScopeTabs({
  activeTab,
  contentClassName,
  onTabChange,
}: {
  activeTab: IdpClientScopeDetailTab
  contentClassName?: string
  onTabChange: (tab: IdpClientScopeDetailTab) => void
}) {
  const { t } = useTranslation('adminIdp')
  const tabs: { id: IdpClientScopeDetailTab; label: string }[] = [
    { id: 'settings', label: t('clientScopes.tabs.settings') },
    { id: 'mappers', label: t('clientScopes.tabs.mappers') },
  ]

  return (
    <div className="relative z-20 shrink-0 bg-white px-5 pb-3 lg:px-[30px]">
      <PageContentBoundary className={contentClassName}>
        <div
          aria-label={t('clientScopes.tabs.label')}
          className="flex h-12 min-w-0 gap-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
        >
          {tabs.map((tab) => {
            const selected = activeTab === tab.id

            return (
              <button
                aria-controls={tabIds[tab.id].panelId}
                aria-selected={selected}
                className={cn(
                  'h-12 shrink-0 cursor-pointer border-b-2 px-1 text-sm font-medium transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]',
                  selected
                    ? 'border-[var(--color-primary)] font-semibold text-[var(--color-text)]'
                    : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
                )}
                id={tabIds[tab.id].tabId}
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                role="tab"
                type="button"
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </PageContentBoundary>
    </div>
  )
}
